import { useContext, useEffect } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  ScaleFade,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiLock } from 'react-icons/fi';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';

import { ReportBIManagement } from '../../../../components/Admin/Reports/ReportBIManagement';
import { HeaderUp } from '../../../../components/_components_0.2/Header_0.2';
import { Sidebar } from '../../../../components/_components_0.2/Sidebar_0.2';
import { SetTitleContext } from '../../../../contexts/SetTitleBIContext';
import { useAuth } from '../../../../hooks/auth';
import { useBiCatalog } from '../../../../hooks/useBi';
import { canSeeBiReport } from '../../../../utils/biAccess';

interface ParamsRouter {
  idReport: string;
}

function Message({ title, hint }: { title: string; hint: string }) {
  return (
    <Center py="16" flexDirection="column" textAlign="center">
      <Icon as={FiLock} fontSize="40" color="gray.500" />
      <Text color="gray.400" mt="3">
        {title}
      </Text>
      <Text color="gray.500" fontSize="sm">
        {hint}
      </Text>
      <Button as={Link} to="/bi-management/dashboard" mt="5" size="sm">
        Ver todos os relatórios
      </Button>
    </Center>
  );
}

export default function PBIRenderization() {
  const { idReport } = useParams<ParamsRouter>();
  const { search } = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const { handleSetTitle } = useContext(SetTitleContext);
  const { data, isLoading, isError, refetch } = useBiCatalog();

  // Links antigos usavam o id do relatório no Power BI (embededId). O mesmo
  // relatório pode estar cadastrado em mais de um grupo: prefere o cadastro
  // que o usuário pode ver.
  const report =
    data?.find(item => item.id === idReport) ||
    data?.find(
      item => item.embededId === idReport && canSeeBiReport(user, item),
    ) ||
    data?.find(item => item.embededId === idReport);
  const canSee = !!report && canSeeBiReport(user, report);

  useEffect(() => {
    if (report && report.id !== idReport) {
      history.replace({
        pathname: `/bi-management/renderization/${report.id}`,
        search,
      });
    }
  }, [report, idReport, history, search]);

  useEffect(() => {
    handleSetTitle(canSee && report ? report.reportName : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee, report?.reportName]);

  useEffect(
    () => () => handleSetTitle(''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex minH="260px" align="center" justify="center">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      );
    }

    // Só mostra o erro se não houver nada em cache: uma falha ao atualizar a
    // lista em segundo plano não pode fechar o relatório que está aberto.
    if (isError && !data) {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar o relatório.
          </AlertDescription>
          <Button size="sm" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </Alert>
      );
    }

    if (!report) {
      return (
        <Message
          title="Relatório não encontrado"
          hint="O link pode estar errado ou o relatório foi removido."
        />
      );
    }

    if (!canSee) {
      return (
        <Message
          title="Você não tem acesso a este relatório"
          hint="Fale com o administrador se precisar dele."
        />
      );
    }

    return <ReportBIManagement key={report.id} report={report} />;
  };

  return (
    <ScaleFade initialScale={0.9} in>
      <HeaderUp path="bi" />
      <Sidebar path="bi" />

      <Box maxW="1480px" mx="auto" px={{ base: 4, md: 6 }} pt="4" pb="6">
        {renderContent()}
      </Box>
    </ScaleFade>
  );
}
